import { useState } from 'react';

/**
 * Test Email Modal Component
 * 
 * Allows users to send test emails to a specific email address
 * 
 * @author SureCost Development Team
 * @version 1.0.0
 */
const TestEmailModal = ({ isOpen, onClose, notificationType, onSend }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSend = async () => {
        setError('');
        
        if (!email.trim()) {
            setError('Please enter an email address');
            return;
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        try {
            setIsLoading(true);
            await onSend(email, notificationType);
            onClose();
            setEmail('');
        } catch (error) {
            setError(error.message || 'Failed to send test email');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setEmail('');
        setError('');
        onClose();
    };

    const getModalTitle = () => {
        switch (notificationType) {
            case 'magicLink':
                return 'Test Magic Link Email';
            case 'welcome':
                return 'Test Welcome Email';
            default:
                return 'Test Email';
        }
    };

    const getModalDescription = () => {
        switch (notificationType) {
            case 'magicLink':
                return 'Send a test magic link authentication email to verify the template and functionality.';
            case 'welcome':
                return 'Send a test welcome email to verify the template and content.';
            default:
                return 'Send a test email to verify the template.';
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideIn {
                        from { transform: translateY(-20px); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }
                `}
            </style>
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    zIndex: 10000,
                    animation: 'fadeIn 0.2s ease-out'
                }}
                onClick={handleClose}
            >
                <div 
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        maxWidth: '28rem',
                        width: '100%',
                        animation: 'slideIn 0.3s ease-out'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div 
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '1.5rem 1.5rem 1rem 1.5rem'
                        }}
                    >
                        <div>
                            <h3 
                                style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#111827',
                                    margin: '0 0 0.25rem 0'
                                }}
                            >
                                {getModalTitle()}
                            </h3>
                            <p 
                                style={{
                                    fontSize: '0.875rem',
                                    color: '#6b7280',
                                    margin: 0
                                }}
                            >
                                {getModalDescription()}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            style={{
                                color: '#9ca3af',
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none',
                                padding: '0.25rem',
                                borderRadius: '0.25rem',
                                transition: 'color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
                            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                        >
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div style={{ padding: '0 1.5rem 1.5rem 1.5rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <label 
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter email address to receive test"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: error ? '1px solid #ef4444' : '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    transition: 'border-color 0.2s',
                                    outline: 'none'
                                }}
                                onFocus={(e) => {
                                    if (!error) e.target.style.borderColor = '#3b82f6';
                                }}
                                onBlur={(e) => {
                                    if (!error) e.target.style.borderColor = '#d1d5db';
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isLoading) {
                                        handleSend();
                                    }
                                }}
                            />
                            {error && (
                                <p style={{
                                    fontSize: '0.75rem',
                                    color: '#ef4444',
                                    marginTop: '0.25rem',
                                    margin: '0.25rem 0 0 0'
                                }}>
                                    {error}
                                </p>
                            )}
                        </div>

                        {/* Info box */}
                        <div style={{
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #e0f2fe',
                            borderRadius: '0.375rem',
                            padding: '0.75rem',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                <svg 
                                    style={{ width: '1rem', height: '1rem', color: '#0ea5e9', marginTop: '0.125rem', flexShrink: 0 }}
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    <p style={{
                                        fontSize: '0.75rem',
                                        color: '#0c4a6e',
                                        margin: 0,
                                        lineHeight: '1.4'
                                    }}>
                                        This will send a test email using the current template configuration. 
                                        {notificationType === 'magicLink' && ' The magic link will be a demo link for testing purposes.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleClose}
                                disabled={isLoading}
                                style={{
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: '#374151',
                                    backgroundColor: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    opacity: isLoading ? 0.5 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLoading) {
                                        e.target.style.backgroundColor = '#f9fafb';
                                        e.target.style.borderColor = '#9ca3af';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isLoading) {
                                        e.target.style.backgroundColor = 'white';
                                        e.target.style.borderColor = '#d1d5db';
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={isLoading || !email.trim()}
                                style={{
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: 'white',
                                    backgroundColor: isLoading || !email.trim() ? '#9ca3af' : '#2563eb',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: isLoading || !email.trim() ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLoading && email.trim()) {
                                        e.target.style.backgroundColor = '#1d4ed8';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isLoading && email.trim()) {
                                        e.target.style.backgroundColor = '#2563eb';
                                    }
                                }}
                            >
                                {isLoading && (
                                    <div style={{
                                        width: '1rem',
                                        height: '1rem',
                                        border: '2px solid rgba(255, 255, 255, 0.3)',
                                        borderTop: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                )}
                                {isLoading ? 'Sending...' : 'Send Test Email'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TestEmailModal;
